/**
 * Teams Area Service (teams-area-chat-shared-todos)
 *
 * API service for the TEAMS area — internal chat spaces + shared team todos.
 *
 * Endpoints:
 * - GET    /api/teams/spaces/                 - My spaces (+unread counts)
 * - POST   /api/teams/spaces/                 - Create a space
 * - GET    /api/teams/spaces/{id}/            - Space detail (members)
 * - PATCH  /api/teams/spaces/{id}/            - Rename (creator/superuser)
 * - DELETE /api/teams/spaces/{id}/            - Delete (creator/superuser)
 * - POST   /api/teams/spaces/{id}/members/    - {add:[], remove:[]}
 * - POST   /api/teams/spaces/{id}/transfer/   - {owner: id} hand over
 * - POST   /api/teams/spaces/{id}/leave/     - Leave the space
 * - POST   /api/teams/spaces/{id}/mute/      - {muted: bool} for me only
 * - GET    /api/teams/spaces/{id}/messages/   - Newest page / ?since= delta /
 *                                              ?before= older page
 * - POST   /api/teams/spaces/{id}/messages/   - Send (reply_to, mention_ids)
 * - PATCH  /api/teams/messages/{id}/          - Edit own message
 * - DELETE /api/teams/messages/{id}/          - Soft-delete (tombstone)
 * - GET    /api/teams/todos/?space={id}       - Shared todo list
 * - POST   /api/teams/todos/                  - Add a shared todo
 * - PATCH  /api/teams/todos/{id}/             - Tick / edit / assign
 * - DELETE /api/teams/todos/{id}/             - Delete a todo
 * - GET    /api/teams/poll/                   - THE single 10s tick
 * - GET    /api/teams/colleagues/             - Member-picker directory
 *
 * Polling contract (anti-hammer): callers poll ONLY while the Teams area is
 * mounted and the tab is visible, one combined request per tick. The backend
 * enforces a dedicated throttle scope ('teams_poll') on top.
 */

import axiosInstance from '../utils/axiosConfig';

const teamsService = {
  async listSpaces() {
    const response = await axiosInstance.get('/api/teams/spaces/');
    return response.data;
  },

  async createSpace({ name, description, memberIds }) {
    const response = await axiosInstance.post('/api/teams/spaces/', {
      name,
      description: description || '',
      member_ids: memberIds || [],
    });
    return response.data;
  },

  async getSpace(spaceId) {
    const response = await axiosInstance.get(`/api/teams/spaces/${spaceId}/`);
    return response.data;
  },

  async updateSpace(spaceId, patch) {
    const response = await axiosInstance.patch(
      `/api/teams/spaces/${spaceId}/`, patch,
    );
    return response.data;
  },

  async deleteSpace(spaceId) {
    await axiosInstance.delete(`/api/teams/spaces/${spaceId}/`);
  },

  /**
   * Hand a space to another member. Creator or superuser only; the target
   * must already be a member. Returns the DETAIL shape, like manageMembers.
   */
  async transferOwnership(spaceId, ownerId) {
    const response = await axiosInstance.post(
      `/api/teams/spaces/${spaceId}/transfer/`, { owner: ownerId },
    );
    return response.data;
  },

  async manageMembers(spaceId, { add = [], remove = [] }) {
    const response = await axiosInstance.post(
      `/api/teams/spaces/${spaceId}/members/`, { add, remove },
    );
    return response.data;
  },

  /**
   * Newest page by default. Pass {since} for the live delta, or {before} to
   * walk backwards through history (which deliberately does NOT mark read).
   * Always resolves to {messages, first_unread_id, has_more}.
   */
  async getMessages(spaceId, { since, before } = {}) {
    const params = new URLSearchParams();
    if (since != null) params.append('since', since);
    if (before != null) params.append('before', before);
    const suffix = params.toString() ? `?${params.toString()}` : '';
    const response = await axiosInstance.get(
      `/api/teams/spaces/${spaceId}/messages/${suffix}`,
    );
    return response.data;
  },

  async postMessage(spaceId, { body, replyTo, mentionIds }) {
    const response = await axiosInstance.post(
      `/api/teams/spaces/${spaceId}/messages/`,
      {
        body,
        reply_to: replyTo || null,
        mention_ids: mentionIds || [],
      },
    );
    return response.data;
  },

  async editMessage(messageId, { body, mentionIds }) {
    const response = await axiosInstance.patch(
      `/api/teams/messages/${messageId}/`,
      { body, mention_ids: mentionIds || [] },
    );
    return response.data;
  },

  async deleteMessage(messageId) {
    await axiosInstance.delete(`/api/teams/messages/${messageId}/`);
  },

  async leaveSpace(spaceId) {
    await axiosInstance.post(`/api/teams/spaces/${spaceId}/leave/`, {});
  },

  async setMuted(spaceId, muted) {
    const response = await axiosInstance.post(
      `/api/teams/spaces/${spaceId}/mute/`, { muted },
    );
    return response.data;
  },

  async listTodos(spaceId) {
    const response = await axiosInstance.get(
      `/api/teams/todos/?space=${spaceId}`,
    );
    return response.data;
  },

  async createTodo({ spaceId, title, assignee, dueDate }) {
    const response = await axiosInstance.post('/api/teams/todos/', {
      space: spaceId,
      title,
      assignee: assignee || null,
      due_date: dueDate || null,
    });
    return response.data;
  },

  async updateTodo(todoId, patch) {
    const response = await axiosInstance.patch(
      `/api/teams/todos/${todoId}/`, patch,
    );
    return response.data;
  },

  async deleteTodo(todoId) {
    await axiosInstance.delete(`/api/teams/todos/${todoId}/`);
  },

  /**
   * The single 10-second tick: spaces summary always; when a space is open,
   * pass its id + the newest message id already on screen to also receive
   * the message delta and the fresh todo list (and advance the read cursor).
   */
  async poll(spaceId, since) {
    const params = new URLSearchParams();
    if (spaceId != null) {
      params.append('space', spaceId);
      if (since != null) {
        params.append('since', since);
      }
    }
    const suffix = params.toString() ? `?${params.toString()}` : '';
    const response = await axiosInstance.get(`/api/teams/poll/${suffix}`);
    return response.data;
  },

  async getColleagues() {
    const response = await axiosInstance.get('/api/teams/colleagues/');
    return response.data;
  },
};

export default teamsService;
